import { useEffect, useState } from "react";
import styles from "../styles/SupporterModal.module.css";
import { generateAuthToken } from "../pages/api/utils";
import apiService from "../pages/api/apiService";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { setAuth } from "../store/authSlice";
import { useRouter } from "next/router";

// Languages login
import enlogin from "../../locales/en/login.json";
import eslogin from "../../locales/es/login.json";

export default function LoginPopup({ onClose }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [emailError, setEmailError] = useState("");
    const [passwordError, setPasswordError] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const Languages = useSelector((state) => state.auth.lang);
    const [loginText, setLoginText] = useState(enlogin);

    const dispatch = useDispatch();
    const router = useRouter();

    const validateEmail = (value) =>
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value);

    useEffect(() => {
        setLoginText(Languages === "en" ? enlogin : eslogin);
    }, [Languages]);

    const handleEmailChange = (e) => {
        const value = e.target.value;
        setEmail(value);

        if (!value) {
            setEmailError(loginText?.email_required || "Email is required");
        } else if (!validateEmail(value)) {
            setEmailError(loginText?.email_invalid || "Invalid email format");
        } else {
            setEmailError("");
        }
    };

    const handlePasswordChange = (e) => {
        const value = e.target.value;
        setPassword(value);
        setPasswordError(value ? "" : loginText?.password_required || "Password is required");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        let valid = true;

        if (!email) {
            setEmailError(loginText?.email_required || "Email is required");
            valid = false;
        } else if (!validateEmail(email)) {
            setEmailError(loginText?.email_invalid || "Invalid email format");
            valid = false;
        }

        if (!password) {
            setPasswordError(loginText?.password_required || "Password is required");
            valid = false;
        }

        if (!valid) {
            setIsSubmitting(false);
            return;
        }
        let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        try {
            const redirectUrl = sessionStorage.getItem("redirectAfterLogin") || "";
            const authTokenGen = generateAuthToken(email);

            const requestData = {
                email,
                password,
                auth_token: authTokenGen,
                last_url: redirectUrl,
                timezone
            };

            const response = await apiService.post("/auth_login", requestData);

            if (response.success) {
                dispatch(
                    setAuth({
                        authToken: response.user_details.auth_token,
                        userId: response.user_details.id,
                        profile_picture: response.user_details.profile_picture,
                        name: response.user_details.name,
                        stripeId: response.user_details.stripe_customer_id,
                        email: response.user_details.email,
                    })
                );

                toast.success(response.message || "Login successful!", {
                    position: "top-right",
                    autoClose: 3000,
                });

                setEmail("");
                setPassword("");
                onClose();

                setTimeout(() => {
                    if (response?.last_url) {
                        router.push(response.last_url);
                    } else {
                        router.push("/dashboard");
                    }
                }, 1000);
            } else {
                toast.error(response.message || "Login failed", {
                    position: "top-right",
                    autoClose: 3000,
                });
            }

            sessionStorage.removeItem("redirectAfterLogin");
        } catch (error) {
            toast.error("Login failed. Please try again.", {
                position: "top-right",
                autoClose: 3000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleForgotPasswordSubmit = async () => {
        if (!email) {
            setEmailError(loginText?.email_required || "Email is required");
            return;
        } else if (!validateEmail(email)) {
            setEmailError(loginText?.email_invalid || "Invalid email format");
            return;
        }

        setIsSubmitting(true);

        const authToken = generateAuthToken(email + "FORGOT_PASSWORD");
        const requestData = { email, auth_token: authToken };

        try {
            const response = await apiService.post("/forgot_password", requestData);

            if (response.success) {
                toast.success(
                    response.message || "Password reset link sent to your email!",
                    { position: "top-right", autoClose: 3000 }
                );
                setShowForgotPassword(false);
                setEmail("");
            } else {
                toast.error(response.message || "Failed to send reset email", {
                    position: "top-right",
                    autoClose: 3000,
                });
            }
        } catch (error) {
            toast.error("Something went wrong. Please try again later.", {
                position: "top-right",
                autoClose: 3000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={`${styles.overlay} d-flex justify-content-center align-items-center`} 
        onClick={(e) => {
        if (e.target.classList.contains(styles.overlay)) {
          onClose();
        }
      }}>
            <div className={styles.bgCircle}></div>
            <div className="card border-0 rounded-4 p-4 position-relative">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className={styles.closeIcon}
                    style={{ width: "32px", height: "32px" }}
                >
                    ✕
                </button>

                {/* Forgot Password Screen */}
                {showForgotPassword ? (
                    <>
                        <div className="login-heading mb-3">
                            {loginText?.forgot_password_title || "Forgot Password"}
                        </div>

                        <div className="mb-3 text-start">
                            <input
                                type="email"
                                className="input w-input form-control loginfield"
                                placeholder={loginText?.email_placeholder}
                                value={email}
                                onChange={handleEmailChange}
                                required
                                disabled={isSubmitting}
                            />
                            {emailError && <small className="text-danger">{emailError}</small>}
                        </div>

                        <button
                            onClick={handleForgotPasswordSubmit}
                            disabled={isSubmitting}
                            className="cta-btn w-100 d-flex justify-content-center"
                        >
                            {isSubmitting ? "Please wait..." : loginText?.submit || "Submit"}
                        </button>

                        <div className="text-center mt-3">
                            <a
                                href="#"
                                className="link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (!isSubmitting) setShowForgotPassword(false);
                                }}
                            >
                                {loginText?.back_to_login || "Back to Login"}
                            </a>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="login-heading mb-3">{loginText?.login}</div>

                        <div className="mb-3 text-start">
                            <input
                                type="email"
                                className="input w-input form-control loginfield"
                                placeholder={loginText?.email_placeholder}
                                value={email}
                                onChange={handleEmailChange}
                                required
                                disabled={isSubmitting}
                            />
                            {emailError && <small className="text-danger">{emailError}</small>}
                        </div>

                        <div className="mb-3 text-start">
                            <div className="input-group">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="input w-input form-control loginfield"
                                    placeholder={loginText?.password_placeholder}
                                    value={password}
                                    onChange={handlePasswordChange}
                                    required
                                    disabled={isSubmitting}
                                />
                                <span
                                    className="input-group-text"
                                    role="button"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                >
                                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                                </span>
                            </div>
                            {passwordError && <small className="text-danger">{passwordError}</small>}
                        </div>

                        <div className="text-start mb-3">
                            <a
                                href="#"
                                className="link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (!isSubmitting) setShowForgotPassword(true);
                                }}
                            >
                                {loginText?.forgot_password}
                            </a>
                        </div>

                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            className="cta-btn w-100 d-flex justify-content-center"
                        >
                            {isSubmitting ? "Please wait..." : loginText?.login}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
